import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { get } from 'core/libs/lodash';

import withTheme from 'core/hocs/withTheme';
import withBreakpoint from 'core/hocs/withBreakpoint';
import withFullscreen from 'core/hocs/withFullscreen';

import SmartImage from 'core/components/SmartImage';
import Position from 'core/components/GridPosition';
import Link from 'core/components/Link';
import CaptionCredits from 'core/components/CaptionCredits';
import AmpImage from 'core/components/SmartImage/Amp';
import withBindProps from 'core/hocs/withBindProps';
import modelPropTypes, { imageAttributes } from 'core/utils/prop-types/model';
import themePropTypes from 'core/utils/prop-types/theme';

import styles from './index.styl';


const themeContract = `
  {
    controls: {
      vikontImage: {
        maxWidthAside,
        maxHeight,
        maxWidth,
        captionMargin,
      },
    },
  }
`;

/**
 * Компонент используется в amp версии.
 * Нельзя использовать: inline стили, кастомные атрибуты и пр.
 */
function ImageWithText(props) {
  const {
    block,
    creditsPrefix,
    isMobile,
    imageComponent: Image,
    theme: {
      controls: {
        vikontImage: vikontImageAtoms,
      },
    },
    topicTitle,
    onImageClick,
  } = props;

  const {
    versions,
    caption,
    credits,
    credits_url: creditsUrl,
    url,
    align,
    alt,
  } = block.attributes;

  const isAside = align === 'left' || align === 'right';
  const isCentered = !isAside || isMobile;
  const desktopLeft = align === 'left' && !isMobile;
  const desktopRight = align === 'right' && !isMobile;

  const wrapperMargin = [isAside, desktopLeft, isAside, desktopRight]
    .map(condition => condition ? '20px' : 0)
    .join(' ');

  const cover = versions.original;

  const wrapperWidth = isCentered || cover.width >= vikontImageAtoms.maxWidth
    ? 'auto'
    : cover.width + 'px';

  const ImageWrapper = url ? Link : Position;

  const imageWrapperProps = url
    ? {
      to: url,
      className: styles.flexibleLink,
    }
    : {
      center: isCentered,
    };

  const titleText = caption || topicTitle;
  const altText = alt || titleText;

  const handleImageClick = useCallback(() => {
    onImageClick({ cover, caption, credits });
  }, [onImageClick, cover, caption, credits]);

  return (
    <div className='wrapper'>
      <style jsx>{`
        .wrapper
          float ${isCentered ? 'none' : align}
          width ${wrapperWidth}
          max-width ${isCentered ? 'none' : vikontImageAtoms.maxWidthAside + 'px'}
          margin ${wrapperMargin}
        .caption
          margin ${vikontImageAtoms.captionMargin}
      `}
      </style>
      <ImageWrapper {...imageWrapperProps}>
        <Image
          versions={versions}
          widthFromSrc
          maxWidth={isAside ? vikontImageAtoms.maxWidthAside : vikontImageAtoms.maxWidth}
          maxHeight={vikontImageAtoms.maxHeight}
          placement='contain'
          useSchema
          alt={altText}
          title={titleText}
          onClick={handleImageClick}
        />
      </ImageWrapper>
      <div className='caption'>
        <CaptionCredits {...{
          caption,
          credits,
          creditsPrefix,
          creditsUrl,
        }}
        />
      </div>
    </div>
  );
}

ImageWithText.defaultProps = {
  imageComponent: SmartImage,
};

ImageWithText.propTypes = {
  /**
   * Данные о самом виджете
   */
  block: modelPropTypes(imageAttributes).isRequired,
  /**
   * Префикс для подписи `credits`.
   *
   * Показывается только если в виджете `block` задан `credits`.
   */
  creditsPrefix: PropTypes.string,
  /** @ignore */
  isMobile: PropTypes.bool,
  /** @ignore */
  theme: themePropTypes(themeContract),
  /**
   * Компонент использующийся для отрисовки картинки, взамен `SmartImage`.
   */
  imageComponent: PropTypes.func,
  /**
   * Атрибуты `alt` и `title` для картинки.
   *
   * Отработает если в виджете не задан `caption`.
   *
   * По умолчанию это заголовок топика `content.attributes.headline`
   */
  topicTitle: PropTypes.string,
  onImageClick: PropTypes.func,
};

const ImageWithTextAndHOCs = withBreakpoint(withTheme(withFullscreen(ImageWithText)));
ImageWithTextAndHOCs.displayName = 'ImageWithText';

export {
  ImageWithTextAndHOCs as ImageWithText,
  ImageWithText as ImageWithTextComponent,
};


export default function image(block, parentProps) {
  const { creditsPrefix, topic, amp } = parentProps;
  const headline = get(topic, 'content.attributes.headline');

  return (
    <ImageWithTextAndHOCs
      block={block}
      creditsPrefix={creditsPrefix}
      topicTitle={headline}
      {...amp && {
        imageComponent: withBindProps({ layout: 'intrinsic' })(AmpImage),
      }}
    />
  );
}
